import { Loader2, ScanSearch } from 'lucide-react';
import { CARD, SECONDARY } from './ui';

interface Props {
  /** Photos in this survey that are not classified yet (the report only uses classified photos). */
  waiting: number;
  total: number;
  hasDraft: boolean;
  /** Photos classified from this screen since the draft was made; the draft does not include them until it is redrafted. */
  classifiedSinceDraft: number;
  /** Present when the host can classify here; absent: the rep is sent to the Photos step. */
  canClassify: boolean;
  isOnline: boolean;
  isClassifying: boolean;
  progress: { done: number; total: number } | null;
  onClassify(): void;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * Says so when photos are waiting to be classified, instead of letting a report silently go out without them. The status region is
 * always mounted (empty when there is nothing to say) so a screen reader announces the text when it appears.
 */
export function PhotoClassifyNotice({ waiting, total, hasDraft, classifiedSinceDraft, canClassify, isOnline, isClassifying, progress, onClassify }: Props) {
  const showWaiting = waiting > 0;
  const showRedraft = !showWaiting && hasDraft && classifiedSinceDraft > 0;
  return (
    <div role="status" aria-live="polite">
      {showRedraft && (
        <div className={`${CARD} border-emerald-200 bg-emerald-50`}>
          <p className="text-sm font-semibold text-emerald-900">
            {classifiedSinceDraft} {plural(classifiedSinceDraft, 'photo was', 'photos were')} classified after this draft was made.
          </p>
          <p className="text-xs text-emerald-800">Redraft to include {plural(classifiedSinceDraft, 'it', 'them')} in the report.</p>
        </div>
      )}
      {showWaiting && (
        <div className={`${CARD} space-y-2 border-amber-200 bg-amber-50`}>
          <p className="text-sm font-semibold text-amber-900">
            {waiting} of {total} {plural(total, 'photo is', 'photos are')} not classified yet, so the report cannot use {plural(waiting, 'it', 'them')}.
          </p>
          <p className="text-xs text-amber-800">
            {!canClassify
              ? 'Open the Photos step and classify them first.'
              : hasDraft
                ? 'Classify them, then Redraft to include them.'
                : 'They are classified for you when you generate the report, or you can do it now.'}
          </p>
          {canClassify && (
            <>
              <button type="button" onClick={onClassify} disabled={!isOnline || isClassifying} className={`${SECONDARY} w-full`}>
                {isClassifying
                  ? <><Loader2 size={16} className="animate-spin" /> Classifying{progress ? ` ${progress.done} of ${progress.total}` : ''}...</>
                  : <><ScanSearch size={16} /> Classify now</>}
              </button>
              {!isOnline && <p className="text-xs text-amber-800">Connect to the internet to classify photos.</p>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
